import React, { useEffect, useMemo, useState } from 'react'
import { ContentCopy, Download, Search } from '@mui/icons-material'

const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename || 'document.txt'
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.setTimeout(() => URL.revokeObjectURL(url), 1500)
}

export default function TextDocumentWorkspace({ blob = null, source = '', filename = 'document.txt', readOnly = false, onSaveFile }) {
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [text, setText] = useState('')
    const [query, setQuery] = useState('')
    const [status, setStatus] = useState('')

    useEffect(() => {
        let cancelled = false

        const load = async () => {
            setLoading(true)
            setError('')
            setStatus('')
            try {
                let nextBlob = blob
                if (!(nextBlob instanceof Blob) && source) {
                    const response = await fetch(source, { credentials: 'include' })
                    if (!response.ok) throw new Error(`Не удалось загрузить файл: HTTP ${response.status}`)
                    nextBlob = await response.blob()
                }
                if (!(nextBlob instanceof Blob)) throw new Error('Текстовый файл недоступен')
                const value = await nextBlob.text()
                if (!cancelled) setText(value)
            } catch (loadError) {
                if (!cancelled) setError(loadError?.message || 'Не удалось открыть текстовый файл')
            } finally {
                if (!cancelled) setLoading(false)
            }
        }

        load()
        return () => { cancelled = true }
    }, [blob, source])

    const matches = useMemo(() => {
        if (!query) return 0
        const needle = query.toLowerCase()
        const haystack = text.toLowerCase()
        let index = 0
        let count = 0
        while ((index = haystack.indexOf(needle, index)) !== -1) {
            count += 1
            index += Math.max(1, needle.length)
        }
        return count
    }, [query, text])

    if (loading) return <div className="text-document-workspace__state">Загружаем документ…</div>
    if (error) return <div className="text-document-workspace__state is-error">{error}</div>

    return (
        <div className="text-document-workspace">
            <div className="text-document-workspace__toolbar">
                <label>
                    <Search />
                    <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Поиск по тексту" />
                    {query ? <span>{matches}</span> : null}
                </label>

                <div>
                    <button
                        type="button"
                        onClick={async () => {
                            try {
                                await navigator.clipboard?.writeText?.(text)
                                setStatus('Текст скопирован')
                            } catch (_error) {
                                setStatus('Не удалось скопировать текст')
                            }
                        }}
                    >
                        <ContentCopy />
                        Копировать
                    </button>
                    <button
                        type="button"
                        className="is-primary"
                        onClick={async () => {
                            const output = new Blob([text], { type: 'text/plain;charset=utf-8' })
                            if (typeof onSaveFile === 'function') {
                                await onSaveFile({ blob: output, filename, mimeType: output.type, kind: 'text' })
                                setStatus('Изменённая копия передана на сохранение')
                            } else {
                                downloadBlob(output, filename)
                                setStatus('Копия сохранена')
                            }
                        }}
                    >
                        <Download />
                        Скачать копию
                    </button>
                </div>
            </div>

            <textarea
                value={text}
                readOnly={readOnly}
                spellCheck={false}
                onChange={(event) => setText(event.target.value)}
                aria-label={filename}
            />

            <div className="text-document-workspace__footer">
                <span>{readOnly ? 'Только просмотр' : 'Можно редактировать локальную копию'}</span>
                {status ? <b>{status}</b> : null}
            </div>
        </div>
    )
}
